import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { Text } from "@nbs/ui";
import { toLandCoverChart, toLandCoverYearShares } from "@/features/analytics/utils/land-cover";
import { filterLandCoverRowsByPublishedYears } from "@/features/story/data/services";
import { NbsDonutChart } from "@/features/charts/NbsDonutChart";
import type { LandscapeId } from "@/features/story/data/landscapes";
import { HUB_OVERVIEW_QUERY } from "../api/documents";
import {
  landCoverClassColor,
  landCoverMapSrc,
  resolveLandCoverMap,
  type LandCoverMapYear,
} from "../data/land-cover-maps";
import { useHubScope } from "../state/HubScopeContext";

type HubLandCoverMapsViewProps = {
  readonly landscapeId: LandscapeId;
  readonly year: LandCoverMapYear;
};

export function HubLandCoverMapsView({ landscapeId, year }: HubLandCoverMapsViewProps) {
  const { t } = useTranslation();
  const { queryVars } = useHubScope();
  const { asset } = resolveLandCoverMap(landscapeId, year);

  const { data, loading, error, refetch } = useQuery(HUB_OVERVIEW_QUERY, {
    variables: {
      ...queryVars,
      dateFrom: "2005-01-01",
      dateTo: "2025-12-31",
    },
    fetchPolicy: "cache-and-network",
  });

  const chart = useMemo(() => {
    const raw = toLandCoverChart(data?.landCoverSeries?.[0]?.points ?? []);
    return {
      ...raw,
      byYear: filterLandCoverRowsByPublishedYears(raw.byYear, landscapeId),
    };
  }, [data, landscapeId]);
  const unit = data?.landCoverSeries?.[0]?.unit ?? "%";
  const plateYear = asset?.year ?? year;

  const shares = useMemo(
    () =>
      toLandCoverYearShares(chart.byYear, plateYear, {
        classKeys: chart.classes,
        maxDirect: 12,
      }),
    [chart, plateYear],
  );
  const donutColors = useMemo(
    () => shares.segments.map((s) => landCoverClassColor(s.name)),
    [shares.segments],
  );
  const fmt = (n: number) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 1 }).format(n);
  const row = chart.byYear.find((r) => String(r.year) === String(plateYear));
  const title = asset?.title ?? t("hub.services.mapMissingTitle");

  return (
    <div className="nbs-hub-ocs" data-testid="hub-services-maps">
      {error ? (
        <div className="nbs-hub-banner nbs-hub-banner--danger" role="alert">
          <Text>{t("hub.services.mapsLoadError")}</Text>
          <button type="button" className="nbs-hub-btn" onClick={() => void refetch()}>
            {t("hub.overview.retry")}
          </button>
        </div>
      ) : null}

      {!asset ? (
        <p className="nbs-hub-muted">{t("hub.services.mapMissingHint")}</p>
      ) : (
        <div className="nbs-hub-ocs__pair" data-testid={`hub-ocs-year-${plateYear}`}>
          <article className="nbs-hub-card nbs-hub-ocs__plate">
            <header className="nbs-hub-card__head">
              <h3>{title}</h3>
            </header>
            <figure className="nbs-hub-ocs__figure">
              <img
                className="nbs-hub-ocs__img"
                src={landCoverMapSrc(asset.fileName)}
                alt={title}
                loading="eager"
                decoding="async"
              />
            </figure>
          </article>

          <aside className="nbs-hub-card nbs-hub-ocs__stats">
            <header className="nbs-hub-card__head">
              <h3>{title}</h3>
            </header>

            {loading && shares.segments.length === 0 ? (
              <p className="nbs-hub-muted">{t("hub.overview.loading")}</p>
            ) : shares.segments.length === 0 ? (
              <p className="nbs-hub-muted">{t("hub.overview.noLandCover")}</p>
            ) : (
              <>
                <div className="nbs-hub-ocs__donut">
                  <NbsDonutChart
                    data={shares.segments}
                    height={220}
                    colors={donutColors}
                    unit="%"
                    showLegend={false}
                  />
                </div>
                <ul className="nbs-hub-ocs__legend">
                  {shares.segments.map((seg) => {
                    const raw = row?.[seg.name];
                    const absolute =
                      typeof raw === "number" ? raw : raw != null ? Number(raw) : null;
                    return (
                      <li key={seg.name}>
                        <span
                          className="nbs-hub-ocs__swatch"
                          style={{ background: landCoverClassColor(seg.name) }}
                          aria-hidden="true"
                        />
                        <span className="nbs-hub-ocs__legend-label">{seg.name}</span>
                        <span className="nbs-hub-ocs__legend-value">
                          {absolute != null && Number.isFinite(absolute)
                            ? `${fmt(absolute)} ${unit}`
                            : "—"}
                          <small> · {fmt(seg.value)}%</small>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}
